// code-guardian-ignore-duplicates: shared structural skeleton / cross-app type mirror — intentional pattern duplication (scheduler lifecycle, CRS-gateway wrappers, web/api type mirrors with no shared package), not copy-pasted logic; consolidating (base class / shared types pkg) is a separate refactor
/**
 * Public stats page (`/stats`).
 *
 * Openly accessible — no auth required. Renders pool + cache health data
 * fetched from `GET /api/public/stats`. Auto-refreshes every 5 seconds.
 *
 * Useful for:
 *   - Verifying the API is reachable + healthy without logging in
 *   - Watching pool counters during a deploy or under burst load
 *   - Confirming the read/write split is active (after infra flips
 *     `ONEOFFICE_DB_READER_HOST`, the reader pool's counters diverge from the
 *     writer's; the page surfaces `readWriteSplitActive` as a clear banner)
 *
 * No infra details (hosts, DB names, env var names) are exposed by the
 * underlying endpoint — see `apps/api/src/routes/public-stats.routes.ts`
 * for the sanitization contract.
 */
import { useCallback, useEffect, useState } from 'react';
import { InlineLoader } from '../components/loading';

interface PoolStats {
  configuredLimit: number;
  poolCreated: boolean;
  totalConnections: number | null;
  idleConnections: number | null;
  busyConnections: number | null;
  waitingRequests: number | null;
}

interface ReaderPoolStats extends PoolStats {
  aliasedToWriter: boolean;
}

interface PgPoolStats {
  configuredMax: number;
  totalCount: number;
  idleCount: number;
  busyCount: number;
  waitingCount: number;
}

interface CacheEntryStats {
  name: string;
  size: number;
  hits: number;
  misses: number;
  hitRate: number;
  approxMemoryBytes: number;
}

interface HeapStats {
  heapUsedBytes: number;
  heapTotalBytes: number;
  rssBytes: number;
  utilization: number;
  peakUtilization: number;
  peakHeapUsedBytes: number;
  consecutiveOverThreshold: number;
}

interface StatsPayload {
  status: 'success';
  timestamp: string;
  pools: {
    mysqlWriter: PoolStats;
    mysqlReader: ReaderPoolStats;
    postgres: PgPoolStats;
  };
  cache: {
    caches: CacheEntryStats[];
    totalSize: number;
    totalHits: number;
    totalMisses: number;
    overallHitRate: number;
    totalMemoryBytes: number;
  };
  heap: HeapStats;
  readWriteSplitActive: boolean;
}

const REFRESH_INTERVAL_MS = 5_000;

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatPct = (n: number): string => `${(n * 100).toFixed(1)}%`;

const formatNumOrDash = (n: number | null): string =>
  n === null || n === undefined ? '—' : String(n);

const getApiBase = (): string => {
  const rawApiUrl = (import.meta as any).env?.VITE_API_URL as string | undefined;
  return rawApiUrl ? rawApiUrl.replace(/\/+$/, '') : '/api';
};

// Color hint for pool utilization — green/amber/red based on busy/limit ratio.
const utilColor = (busy: number | null, limit: number): string => {
  if (busy === null || limit === 0) return 'text-gray-700';
  const pct = busy / limit;
  if (pct >= 0.85) return 'text-red-600 font-semibold';
  if (pct >= 0.6) return 'text-amber-600 font-medium';
  return 'text-emerald-700';
};

export default function StatsPage() {
  const [data, setData] = useState<StatsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchAt, setLastFetchAt] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBase()}/public/stats`, {
        // Bust any intermediate cache; we want fresh numbers each tick.
        cache: 'no-store',
      });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const json = (await res.json()) as StatsPayload;
      setData(json);
      setLastFetchAt(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    }
  }, []);

  useEffect(() => {
    void fetchStats();
    if (!autoRefresh) return;
    const id = setInterval(fetchStats, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchStats, autoRefresh]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ChannelPlex — System Stats</h1>
            <p className="text-sm text-gray-500 mt-1">
              Live pool + cache health. Public — no login required.
            </p>
          </div>
          <div className="text-right">
            <button
              onClick={() => setAutoRefresh((v) => !v)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                autoRefresh
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {autoRefresh ? '⏸ Pause' : '▶ Resume'} auto-refresh
            </button>
            <p className="text-xs text-gray-400 mt-1">
              {lastFetchAt
                ? `Last update: ${lastFetchAt.toLocaleTimeString()}`
                : 'Loading…'}
            </p>
          </div>
        </div>

        {/* Banner: read/write split status */}
        {data && (
          <div
            className={`mb-6 rounded-md border px-4 py-3 text-sm ${
              data.readWriteSplitActive
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            {data.readWriteSplitActive ? (
              <>
                <strong>Read/Write split ACTIVE.</strong> Reader pool is independent of writer. Historical reads route to the replica.
              </>
            ) : (
              <>
                <strong>Read/Write split dormant.</strong> All reads currently route through the writer pool (no <code className="font-mono text-xs bg-amber-100 px-1 rounded">ONEOFFICE_DB_READER_HOST</code> configured). The split becomes active once infra provisions the read replica.
              </>
            )}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <strong>Failed to load stats:</strong> {error}
          </div>
        )}

        {/* Pool stats */}
        {data && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Connection Pools</h2>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Pool</th>
                    <th className="px-4 py-3 text-right">Limit</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right">Idle</th>
                    <th className="px-4 py-3 text-right">Busy</th>
                    <th className="px-4 py-3 text-right">Waiting</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">MySQL Writer</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {data.pools.mysqlWriter.configuredLimit}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatNumOrDash(data.pools.mysqlWriter.totalConnections)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatNumOrDash(data.pools.mysqlWriter.idleConnections)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right ${utilColor(
                        data.pools.mysqlWriter.busyConnections,
                        data.pools.mysqlWriter.configuredLimit
                      )}`}
                    >
                      {formatNumOrDash(data.pools.mysqlWriter.busyConnections)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatNumOrDash(data.pools.mysqlWriter.waitingRequests)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      MySQL Reader{' '}
                      {data.pools.mysqlReader.aliasedToWriter && (
                        <span className="ml-1 text-xs text-amber-600 font-normal">
                          (aliased to writer)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {data.pools.mysqlReader.configuredLimit}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatNumOrDash(data.pools.mysqlReader.totalConnections)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatNumOrDash(data.pools.mysqlReader.idleConnections)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right ${utilColor(
                        data.pools.mysqlReader.busyConnections,
                        data.pools.mysqlReader.configuredLimit
                      )}`}
                    >
                      {formatNumOrDash(data.pools.mysqlReader.busyConnections)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatNumOrDash(data.pools.mysqlReader.waitingRequests)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">PostgreSQL</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {data.pools.postgres.configuredMax}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {data.pools.postgres.totalCount}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {data.pools.postgres.idleCount}
                    </td>
                    <td
                      className={`px-4 py-3 text-right ${utilColor(
                        data.pools.postgres.busyCount,
                        data.pools.postgres.configuredMax
                      )}`}
                    >
                      {data.pools.postgres.busyCount}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {data.pools.postgres.waitingCount}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              <span className="text-emerald-700">Green</span> = healthy headroom (busy &lt; 60% of limit).{' '}
              <span className="text-amber-600">Amber</span> = approaching capacity (60–85%).{' '}
              <span className="text-red-600">Red</span> = saturated (≥85%).
            </p>
          </section>
        )}

        {/* Cache stats */}
        {data && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">In-Memory Caches</h2>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Cache</th>
                    <th className="px-4 py-3 text-right">Entries</th>
                    <th className="px-4 py-3 text-right">Hits</th>
                    <th className="px-4 py-3 text-right">Misses</th>
                    <th className="px-4 py-3 text-right">Hit Rate</th>
                    <th className="px-4 py-3 text-right">Memory</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.cache.caches.map((c) => (
                    <tr key={c.name}>
                      <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{c.size}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{c.hits}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{c.misses}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatPct(c.hitRate)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        {formatBytes(c.approxMemoryBytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-4 py-3 font-semibold text-gray-900">TOTAL</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {data.cache.totalSize}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {data.cache.totalHits}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {data.cache.totalMisses}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatPct(data.cache.overallHitRate)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatBytes(data.cache.totalMemoryBytes)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Hit rate &lt; 50% on a near-static lookup usually means the TTL is too short or write-through invalidation is too aggressive.
            </p>
          </section>
        )}

        {/* Heap stats */}
        {data && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Process Heap</h2>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900 w-1/3">Current Utilization</td>
                    <td
                      className={`px-4 py-3 text-right ${
                        data.heap.utilization >= 0.7
                          ? 'text-red-600 font-semibold'
                          : data.heap.utilization >= 0.5
                          ? 'text-amber-600 font-medium'
                          : 'text-emerald-700'
                      }`}
                    >
                      {formatPct(data.heap.utilization)}
                      {data.heap.consecutiveOverThreshold > 0 && (
                        <span className="ml-2 text-xs text-amber-600">
                          (over threshold for {data.heap.consecutiveOverThreshold} ticks)
                        </span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Heap Used / Total</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatBytes(data.heap.heapUsedBytes)} / {formatBytes(data.heap.heapTotalBytes)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">RSS</td>
                    <td className="px-4 py-3 text-right text-gray-700">{formatBytes(data.heap.rssBytes)}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-gray-900">Peak Utilization (since process start)</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {formatPct(data.heap.peakUtilization)} · {formatBytes(data.heap.peakHeapUsedBytes)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Heap monitor alerts via email when utilization stays above 70% for 5+ consecutive minute checks. Single-replica means an OOM kill is a full outage — peak utilization is worth watching.
            </p>
          </section>
        )}

        {/* Loading state */}
        {!data && !error && (
          <div className="text-center py-12 text-gray-500 text-sm"><InlineLoader label="Loading stats…" /></div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-8">
          Auto-refreshes every {REFRESH_INTERVAL_MS / 1000}s · Counters are cumulative since the API process started · No login required
        </p>
      </div>
    </div>
  );
}
